import { Controller } from "@hotwired/stimulus"

import consumer from "../channels/consumer"
import { sanitizeHtml } from "../helpers/sanitize_html"


export default class extends Controller {
    static targets = ["notifications", "loading", "scroll", "sentinel", "dismissDialog"];
    static values = { url: String };


    audio;
    connect() {
        this.loaded = false;
        this.loading = false;
        this.observer = null;

        this.audio = new Audio('https://app.ukcil.com/pop.mp3');
        this.subscription = consumer.subscriptions.create(
            {
                channel: "NotificationsChannel",
                id: this.data.get("id"),
            },
            {
                connected: this._connected.bind(this),
                disconnected: this._disconnected.bind(this),
                received: this._received.bind(this)
            }
        );

        window.vapidPublicKey = new Uint8Array(this.data.get("pk").slice(1, this.data.get("pk").length - 1).split(',').flatMap((x) => Number(x)));
        let subURL = "/home/account/" + this.data.get("id") + "/web_push_subscription"
        if ((navigator.serviceWorker) && ('PushManager' in window)) {
            navigator.serviceWorker.register(this.data.get("reg")).then(function (reg) {
                navigator.serviceWorker.ready.then((serviceWorkerRegistration) => {
                    serviceWorkerRegistration.pushManager
                        .subscribe({
                            userVisibleOnly: true,
                            applicationServerKey: window.vapidPublicKey
                        }).then(async function (sub) {
                        const data = await fetch(subURL, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
                            },
                            body: JSON.stringify(sub),
                        }).then(r => r.json());
                    });
                });
            });
        } else {
            console.error('Service Worker is not supported in this browser');
        }

    }

    _connected() {

    }

    _disconnected() {

    }

    disconnect() {
        this.observer?.disconnect();

        if (this.subscription) {
            consumer.subscriptions.remove(this.subscription);
            this.subscription = null;
        }
    }

    _received(data) {
        if (data.action == 'add' && this.loaded) {
            this.notificationsTarget.querySelector('[data-notifications-empty]')?.remove();
            this.notificationsTarget.insertAdjacentHTML("afterbegin", sanitizeHtml(data.html));
            this.audio.play();
        }
    }

    async load() {
        if (this.loaded || this.loading) return;

        await this.fetchPage(this.urlValue, true);
    }

    async loadMore() {
        if (this.loading || !this.hasSentinelTarget) return;

        const nextUrl = this.sentinelTarget.dataset.nextUrl;
        if (nextUrl) await this.fetchPage(nextUrl, false);
    }

    async fetchPage(url, initialLoad) {
        this.loading = true;
        this.observer?.disconnect();

        if (initialLoad) this.loadingTarget.classList.remove("hidden");

        try {
            const response = await fetch(url, {
                headers: {
                    "Accept": "text/html",
                    "X-Requested-With": "XMLHttpRequest"
                }
            });

            if (!response.ok) throw new Error(`Notification request failed (${response.status})`);

            const html = sanitizeHtml(await response.text());

            if (initialLoad) {
                this.notificationsTarget.innerHTML = html;
                this.loaded = true;
            } else {
                const sentinel = this.sentinelTarget;
                sentinel.insertAdjacentHTML("beforebegin", html);
                sentinel.remove();
            }

            this.observeSentinel();
        } catch (error) {
            if (initialLoad) {
                this.notificationsTarget.innerHTML = `
                    <div class="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800" role="alert">
                        Notifications could not be loaded. Close and reopen the panel to try again.
                    </div>`;
            }

            console.error(error);
        } finally {
            this.loading = false;
            if (initialLoad) this.loadingTarget.classList.add("hidden");
        }
    }

    observeSentinel() {
        this.observer?.disconnect();
        if (!this.hasSentinelTarget) return;

        this.observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this.loadMore();
        }, {
            root: this.hasScrollTarget ? this.scrollTarget : null,
            rootMargin: "0px 0px 160px 0px"
        });

        this.observer.observe(this.sentinelTarget);
    }

    // Function to handle "x" button click for individual notifications
    hideAndMarkAsRead(event) {
        event.preventDefault();
        const notificationElement = event.currentTarget.closest('.notification');

        // Hide the notification element
        if (notificationElement) {
            notificationElement.classList.add('hidden');
        }

        // Make the HTTP request to mark the notification as read
        fetch(event.currentTarget.href, {
            method: "PUT",
            headers: {
                'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
            }
        });
    }

    openDismissDialog() {
        this.dismissDialogTarget.showModal();
    }

    closeDismissDialog() {
        this.dismissDialogTarget.close();
    }

    closeDismissDialogFromBackdrop(event) {
        if (event.target === this.dismissDialogTarget) this.closeDismissDialog();
    }

    async dismissAll(event) {
        const button = event.currentTarget;
        const originalLabel = button.textContent;
        button.disabled = true;
        button.textContent = "Dismissing…";

        try {
            const response = await fetch(button.dataset.dismissUrl, {
                method: "PUT",
                headers: {
                    "Accept": "application/json",
                    "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
                }
            });

            if (!response.ok) throw new Error(`Dismiss notifications request failed (${response.status})`);

            this.closeDismissDialog();
            this.removeAllNotifications();
        } catch (error) {
            button.disabled = false;
            button.textContent = originalLabel;
            console.error(error);
        }
    }

    removeAllNotifications() {
        this.observer?.disconnect();
        this.loaded = true;
        this.notificationsTarget.innerHTML = `
            <div class="rounded-2xl border border-dashed border-slate-300 bg-white px-5 py-10 text-center" data-notifications-empty>
                <p class="font-semibold text-slate-900">You are all caught up</p>
                <p class="mt-1 text-sm text-slate-500">New notifications will appear here.</p>
            </div>`;
    }
}
